import { 
    format, 
    formatDistanceToNow, 
    isAfter, 
    isBefore, 
    subHours, 
    addMinutes,
    isValid,
    parseISO
} from 'date-fns';

/**
 * Formats a date string into a human-readable format
 * @param dateString - The date string to format (ISO format or similar)
 * @returns Formatted date string (e.g., "15 June 2023")
 */
export const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        return date.toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    } catch {
        return 'Invalid date';
    }
};

/**
 * Formats a timestamp for display in the background services dashboard
 * @param timestamp - The timestamp to format (Date object, ISO string, or null)
 * @returns Formatted timestamp string
 */
export const formatTimestamp = (timestamp: Date | string | null): string => {
    if (!timestamp) return 'Never executed';
    
    try {
        const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
        if (!isValid(date)) return 'Invalid date';
        
        return format(date, 'MMM dd, yyyy HH:mm:ss');
    } catch {
        return 'Invalid date';
    }
};

/**
 * Formats a timestamp as a relative time (e.g., "2 hours ago", "in 30 minutes")
 * @param timestamp - The timestamp to format (Date object, ISO string, or null)
 * @returns Relative time string
 */
export const formatRelativeTime = (timestamp: Date | string | null): string => {
    if (!timestamp) return 'Never';
    
    try {
        const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
        if (!isValid(date)) return 'Invalid date';
        
        const now = new Date();
        
        if (isAfter(date, now)) {
            // Future date - show "in X time"
            return `in ${formatDistanceToNow(date)}`;
        } else {
            // Past date - show "X time ago"
            return `${formatDistanceToNow(date)} ago`;
        }
    } catch {
        return 'Invalid date';
    }
};

/**
 * Determines if a timestamp indicates recent execution (within 1 hour)
 * @param timestamp - The timestamp to check
 * @returns True if the execution was recent
 */
export const isRecentExecution = (timestamp: Date | string | null): boolean => {
    if (!timestamp) return false;
    
    try {
        const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
        if (!isValid(date)) return false;
        
        const oneHourAgo = subHours(new Date(), 1);
        return isAfter(date, oneHourAgo);
    } catch {
        return false;
    }
};

/**
 * Determines if a scheduled timestamp indicates an upcoming execution (within 30 minutes)
 * @param timestamp - The timestamp to check
 * @returns True if the execution is scheduled soon
 */
export const isUpcomingExecution = (timestamp: Date | string | null): boolean => {
    if (!timestamp) return false;
    
    try {
        const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
        if (!isValid(date)) return false;
        
        const now = new Date();
        const thirtyMinutesFromNow = addMinutes(now, 30);
        
        return isAfter(date, now) && isBefore(date, thirtyMinutesFromNow);
    } catch {
        return false;
    }
};

/**
 * Determines if a service is overdue for execution
 * @param lastExecution - The last execution timestamp
 * @param nextExecution - The next scheduled execution timestamp
 * @returns True if the service is overdue
 */
export const isOverdueExecution = (
    _lastExecution: Date | string | null,
    nextExecution: Date | string | null
): boolean => {
    if (!nextExecution) return false;
    
    try {
        const nextDate = typeof nextExecution === 'string' ? parseISO(nextExecution) : nextExecution;
        if (!isValid(nextDate)) return false;
        
        const now = new Date();
        return isBefore(nextDate, now);
    } catch {
        return false;
    }
};

/**
 * Gets the appropriate CSS classes for timestamp styling based on execution status
 * @param timestamp - The timestamp to evaluate
 * @param isLastExecution - Whether this is a last execution timestamp (vs next execution)
 * @returns CSS class string for styling
 */
export const getTimestampStatusClasses = (
    timestamp: Date | string | null,
    isLastExecution: boolean = true
): string => {
    if (!timestamp) return 'text-gray-500';
    
    if (isLastExecution) {
        return isRecentExecution(timestamp) 
            ? 'text-green-400' 
            : 'text-gray-300';
    } else {
        // Next execution timestamp
        if (isUpcomingExecution(timestamp)) {
            return 'text-yellow-400';
        }
        if (isOverdueExecution(null, timestamp)) {
            return 'text-red-400';
        }
        return 'text-blue-400';
    }
};

/**
 * Formats file size in bytes to a human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number = 0): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const fileSize = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${fileSize} ${sizes[i]}`;
};
